/* ============================================================
   SUKHARA — MOBILE-FIRST CHROME (v1)
   - Search bottom sheet open/close + predictive
   - Recent searches (localStorage)
   - Wishlist count badge sync
   - Cart count badge sync (via Shopify cart bubble events)
   - Tab bar interactions
   ============================================================ */

(function () {
  if (typeof window === 'undefined') return;

  // ------------ Helpers ------------
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  // ------------ 1. Search bottom sheet ------------
  const search = {
    sheet: null,
    input: null,
    body: null,
    results: null,
    resultsList: null,
    seeAll: null,
    recentSection: null,
    recentList: null,
    debounce: null,
    RECENT_KEY: 'sk_recent_searches',

    init() {
      this.sheet = $('[data-sk-search]');
      if (!this.sheet) return;

      this.input = $('[data-sk-search-input]', this.sheet);
      this.body = $('[data-sk-search-body]', this.sheet);
      this.results = $('[data-sk-search-results]', this.sheet);
      this.resultsList = $('[data-sk-search-results-list]', this.sheet);
      this.seeAll = $('[data-sk-search-see-all]', this.sheet);
      this.recentSection = $('[data-sk-search-recent]', this.sheet);
      this.recentList = $('[data-sk-search-recent-list]', this.sheet);

      // Open triggers
      $$('[data-sk-search-open]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close trigger
      $$('[data-sk-search-close]', this.sheet).forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Backdrop tap closes
      this.sheet.addEventListener('click', (e) => {
        if (e.target === this.sheet) this.close();
      });

      // ESC closes
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      // Predictive search on input
      this.input.addEventListener('input', () => {
        const q = this.input.value.trim();
        clearTimeout(this.debounce);
        if (q.length < 2) {
          this.showDefault();
          return;
        }
        this.debounce = setTimeout(() => this.fetchResults(q), 220);
      });

      // Form submit → save recent + go to /search
      const form = $('[data-sk-search-form]', this.sheet);
      form.addEventListener('submit', (e) => {
        const q = this.input.value.trim();
        if (q) this.saveRecent(q);
        // let browser do the GET to /search
      });

      this.renderRecent();
    },

    isOpen() {
      return this.sheet && this.sheet.getAttribute('aria-hidden') === 'false';
    },

    open() {
      if (!this.sheet) return;
      this.sheet.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sk-search-open');
      // Slight delay to ensure CSS transition triggers + iOS keyboard plays nice
      setTimeout(() => this.input && this.input.focus(), 80);
      this.renderRecent();
    },

    close() {
      if (!this.sheet) return;
      this.sheet.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sk-search-open');
      if (this.input) this.input.blur();
    },

    showDefault() {
      this.body.hidden = false;
      this.results.hidden = true;
    },

    showResults() {
      this.body.hidden = true;
      this.results.hidden = false;
    },

    async fetchResults(q) {
      this.showResults();
      this.resultsList.innerHTML = '<p class="sk-search__loading">Searching…</p>';
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
        const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
        const data = await r.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (products.length === 0) {
          this.resultsList.innerHTML = `<p class="sk-search__empty">No products match "${this.escapeHtml(q)}".<br>Try "cushion", "curtain", or "terracotta".</p>`;
          if (this.seeAll) this.seeAll.style.display = 'none';
          return;
        }
        this.resultsList.innerHTML = products.map(p => this.resultHtml(p)).join('');
        if (this.seeAll) {
          this.seeAll.style.display = 'block';
          this.seeAll.href = `/search?q=${encodeURIComponent(q)}`;
          this.seeAll.textContent = `See all results for "${q}" →`;
        }
      } catch (err) {
        this.resultsList.innerHTML = '<p class="sk-search__empty">Search unavailable. Try again.</p>';
      }
    },

    resultHtml(p) {
      const img = p.featured_image || p.image || '';
      const url = p.url || '#';
      const price = (p.price && typeof p.price === 'string') ? p.price :
                    (p.price && typeof p.price === 'number') ? this.formatMoney(p.price) : '';
      return `
        <a href="${url}" class="sk-search-result" data-sk-result>
          ${img ? `<img class="sk-search-result__img" src="${img}" alt="" loading="lazy">` : '<div class="sk-search-result__img"></div>'}
          <div class="sk-search-result__info">
            <span class="sk-search-result__title">${this.escapeHtml(p.title || '')}</span>
            ${price ? `<span class="sk-search-result__price">${price}</span>` : ''}
          </div>
        </a>`;
    },

    formatMoney(n) {
      // Shopify suggest returns integer cents on price; safe fallback
      const v = typeof n === 'number' ? n / 100 : n;
      return `₹${Math.round(v).toLocaleString('en-IN')}`;
    },

    escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    },

    saveRecent(q) {
      const list = this.getRecent();
      const next = [q, ...list.filter(x => x.toLowerCase() !== q.toLowerCase())].slice(0, 6);
      try { localStorage.setItem(this.RECENT_KEY, JSON.stringify(next)); } catch (e) {}
    },

    getRecent() {
      try { return JSON.parse(localStorage.getItem(this.RECENT_KEY) || '[]'); } catch (e) { return []; }
    },

    renderRecent() {
      if (!this.recentSection || !this.recentList) return;
      const list = this.getRecent();
      if (list.length === 0) {
        this.recentSection.hidden = true;
        return;
      }
      this.recentSection.hidden = false;
      this.recentList.innerHTML = list.map(q =>
        `<li><a href="/search?q=${encodeURIComponent(q)}" class="sk-search__chip">${this.escapeHtml(q)}</a></li>`
      ).join('');
    }
  };

  // ------------ 2. Wishlist count badge ------------
  const wishlist = {
    KEY: 'sk_wishlist',
    init() {
      this.update();
      window.addEventListener('storage', (e) => {
        if (e.key === this.KEY) this.update();
      });
      // Public API: window.SukharaWishlist.add('handle') / remove / toggle
      window.SukharaWishlist = {
        get: () => this.get(),
        add: (handle) => { this.toggle(handle, true); },
        remove: (handle) => { this.toggle(handle, false); },
        toggle: (handle) => { this.toggle(handle); },
        has: (handle) => this.get().includes(handle)
      };
    },
    get() {
      try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch (e) { return []; }
    },
    set(arr) {
      try { localStorage.setItem(this.KEY, JSON.stringify(arr)); } catch (e) {}
      this.update();
    },
    toggle(handle, force) {
      const list = this.get();
      const has = list.includes(handle);
      let next;
      if (typeof force === 'boolean') {
        next = force ? (has ? list : [...list, handle]) : list.filter(h => h !== handle);
      } else {
        next = has ? list.filter(h => h !== handle) : [...list, handle];
      }
      this.set(next);
      return next.includes(handle);
    },
    update() {
      const count = this.get().length;
      $$('[data-sk-wishlist-count]').forEach(el => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }
  };

  // ------------ 3. Cart count badge sync ------------
  const cartBadge = {
    init() {
      this.refresh();
      // Listen for Shopify section render events / cart updates
      document.addEventListener('cart:refresh', () => this.refresh());
      document.addEventListener('cart:updated', () => this.refresh());
      // PubSub if theme uses it
      if (window.subscribe) {
        try {
          window.subscribe('cart-update', () => this.refresh());
        } catch (e) {}
      }
      // Fallback: poll on focus
      window.addEventListener('focus', () => this.refresh());
    },
    async refresh() {
      try {
        const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await r.json();
        const count = cart.item_count || 0;
        // Update header cart bubble
        $$('.cart-count-bubble').forEach(el => {
          el.textContent = count;
          el.parentNode && (el.parentNode.style.display = count ? '' : '');
        });
        // Update tab bar badge if there's one for cart in future
        $$('[data-sk-cart-count]').forEach(el => {
          el.textContent = count;
          el.hidden = count === 0;
        });
      } catch (e) {}
    }
  };

  // ------------ 4. PDP sticky cart visibility → hide tab bar ------------
  const pdpStickyHandler = {
    init() {
      const stickies = $$('.sk-pdp__sticky, .product-sticky-cart');
      if (stickies.length === 0) return;
      // When PDP sticky becomes visible, add class to body so tab bar hides
      const obs = new IntersectionObserver((entries) => {
        // We instead watch the trigger — the buy box. When buy box scrolls out of view, sticky is shown.
      });
      // Simpler: when scroll passes a threshold (say buy box bottom), add class
      // Skip for now — sticky bars in current PDP use their own logic
    }
  };

  // ------------ 5. Shop bottom sheet ------------
  const shopSheet = {
    sheet: null,
    init() {
      this.sheet = $('[data-sk-shop]');
      if (!this.sheet) return;
      $$('[data-sk-shop-open]').forEach(btn => btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
      $$('[data-sk-shop-close]', this.sheet).forEach(btn => btn.addEventListener('click', () => this.close()));
      this.sheet.addEventListener('click', (e) => { if (e.target === this.sheet) this.close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen()) this.close(); });
    },
    isOpen() { return this.sheet && this.sheet.getAttribute('aria-hidden') === 'false'; },
    open() {
      if (!this.sheet) return;
      this.sheet.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sk-shop-open');
    },
    close() {
      if (!this.sheet) return;
      this.sheet.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sk-shop-open');
    }
  };

  // ------------ 5b. Product card interactions (Quick Add + Heart) ------------
  const productCard = {
    init() {
      // Use event delegation — works for cards added later (rails, lazy loads, etc.)
      document.addEventListener('click', (e) => {
        const heart = e.target.closest('[data-sk-wishlist]');
        if (heart) {
          e.preventDefault();
          const handle = heart.getAttribute('data-handle');
          if (!handle) return;
          const isActive = window.SukharaWishlist.toggle(handle);
          heart.classList.toggle('is-active', isActive);
          heart.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          return;
        }
      });

      // Initial heart state from localStorage
      this.syncHearts();

      // Quick Add (AJAX submit)
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-sk-card-add-form]');
        if (!form) return;
        e.preventDefault();
        this.quickAdd(form);
      });
    },

    syncHearts() {
      $$('[data-sk-wishlist]').forEach(heart => {
        const handle = heart.getAttribute('data-handle');
        if (handle && window.SukharaWishlist && window.SukharaWishlist.has(handle)) {
          heart.classList.add('is-active');
          heart.setAttribute('aria-pressed', 'true');
        }
      });
    },

    async quickAdd(form) {
      const btn = form.querySelector('[data-sk-card-add]');
      if (!btn) return;
      const originalLabel = btn.innerHTML;
      btn.setAttribute('aria-busy', 'true');
      btn.innerHTML = '<span>Adding…</span>';

      try {
        const formData = new FormData(form);
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' }
        });
        if (!r.ok) throw new Error('Add to cart failed');
        await r.json();
        btn.classList.add('is-added');
        btn.innerHTML = '<span>Added ✓</span>';
        // Refresh cart count badge
        if (cartBadge.refresh) cartBadge.refresh();
        // Dispatch event so the cart drawer (if any) refreshes
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Reset button after 1.6s
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.removeAttribute('aria-busy');
          btn.innerHTML = originalLabel;
        }, 1600);
      } catch (err) {
        btn.removeAttribute('aria-busy');
        btn.innerHTML = '<span>Try again</span>';
        setTimeout(() => { btn.innerHTML = originalLabel; }, 1800);
      }
    }
  };

  // ------------ 6. WhatsApp delay (scroll > 250px OR time > 15s) ------------
  const whatsappDelay = {
    init() {
      const buttons = $$('.sotp-widget, [class*="whatsapp"], #sotp-floating, .pf__whatsapp');
      if (buttons.length === 0) return;
      // Hide initially
      buttons.forEach(b => { b.style.opacity = '0'; b.style.visibility = 'hidden'; b.style.transition = 'opacity 0.3s ease'; });
      let revealed = false;
      const reveal = () => {
        if (revealed) return;
        revealed = true;
        buttons.forEach(b => { b.style.visibility = 'visible'; b.style.opacity = '1'; });
        window.removeEventListener('scroll', onScroll);
      };
      const onScroll = () => { if (window.scrollY > 250) reveal(); };
      window.addEventListener('scroll', onScroll, { passive: true });
      setTimeout(reveal, 15000);
    }
  };

  // ------------ Boot ------------
  function boot() {
    search.init();
    wishlist.init();
    cartBadge.init();
    pdpStickyHandler.init();
    shopSheet.init();
    productCard.init();
    whatsappDelay.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
